/*  iceberg-ui  */
import { Flex } from '@auviknetworks/iceberg-ui';

/* MUI */
import { Typography } from '@mui/material';

/*  React  */
import { CellContext } from '@tanstack/react-table';
import { runReportLinkURL } from 'application/Home/Reports/helpers';

/*  Shared  */
import { usePanelContext } from 'application/Shared/Components/Dashboard/Panels/PanelContext';
import { type TablePanel as TablePanelType } from 'application/Shared/Components/Dashboard/types';
import { KebabMenu } from 'application/Shared/Components/Menu/KebabMenu';
import { Notify } from 'application/Shared/Helpers';
import { calculateScheduledReportTimeRange } from 'application/Shared/Helpers/scheduledReportsDeliveryTime';
import { Confirm } from 'application/Shared/Modals/Confirm';
import { ScheduledReport as ScheduledReportType } from 'application/Shared/types/scheduledReport';

/* Hooks */
import {
    type DeleteScheduledReportPayload,
    useDeleteScheduledReport
} from 'hooks/dataAccess/Reports/scheduledReports';

/* React */
import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';

const deleteReport = async ({
    clientId,
    deleteFn,
    id,
    refetch,
    t
}: {
    clientId?: string;
    id: string;
    deleteFn: (payload: DeleteScheduledReportPayload) => Promise<boolean>;
    refetch: () => void;
    t: TFunction<'translation', undefined>;
}) => {
    const response = await deleteFn({ clientId, id });
    if (response) {
        Notify.s(t('Reports.ScheduledReport.success_remove'));
        refetch();
    } else {
        Notify.e(t('Reports.ScheduledReport.error_removing_report'));
    }
};

/**
 * Component that renders action menu for scheduled reports
 * Provides options to view report details, run report, and delete report
 * @component
 * @param {Object} props - Component props
 * @param {CellContext<ScheduledReportType, unknown>} props.row - Table row context containing report data
 * @returns {React.ReactElement} Rendered cell with kebab menu and confirmation modal
 */
export const ReportActionsCell = ({
    row
}: CellContext<ScheduledReportType, unknown>): React.ReactElement => {
    const { t } = useTranslation();
    const { partnerId, clientId } = useParams();
    const [, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { deleteScheduleReport } = useDeleteScheduledReport();

    const {
        queryResults: { refetch }
    } = usePanelContext<TablePanelType>();

    const [promptDelete, setPromptDelete] = useState(false);

    // Based on the scheduled report period, we need to run this for the current day/week/month/quarter/customQuarter
    // therefore, we need to overwrite the start and end dates.
    const { startTime, endTime } = calculateScheduledReportTimeRange({
        dateCreated: row.original.dateCreated,
        period: row.original.params.period
    });

    const to = runReportLinkURL({
        clientId,
        partnerId,
        reportParams: { ...row.original.params, endTime, startTime }
    });

    const options = [
        {
            dataTrackid: `reportActionsCell_viewReportDetails`,
            key: 'viewDetails',
            label: t('Reports.viewReportDetails'),
            value: 'viewDetails'
        },
        {
            dataTrackid: `runReport`,
            key: 'runReport',
            label: t('Reports.run_report'),
            value: 'runReport'
        },
        {
            dataTrackid: `deleteReport`,
            key: 'deleteReport',
            label: t('Common.delete'),
            value: 'deleteReport'
        }
    ];

    const handleShowViewConfiguration = (id: string) => {
        setSearchParams((currentParams) => {
            if (id) {
                currentParams.set('scheduledReportId', id);
            } else {
                currentParams.delete('scheduledReportId');
            }
            return new URLSearchParams(currentParams);
        });
    };

    return (
        <Flex justifyContent="flex-end">
            <KebabMenu
                data-trackid={`reportActionsCell_${row.original.id}`}
                options={options}
                onClick={(
                    value: 'runReport' | 'viewDetails' | 'deleteReport'
                ) => {
                    if (value === 'runReport') {
                        navigate(to);
                    }
                    if (value === 'viewDetails') {
                        handleShowViewConfiguration(row.original.id);
                    }
                    if (value === 'deleteReport') {
                        setPromptDelete(true);
                    }
                }}
                data-testid={`reportActionsCell_${row.original.id}`}
            />
            {promptDelete && (
                <Confirm
                    data-testid="confirmDeleteScheduledReport"
                    data-trackid="confirmDeleteScheduledReport"
                    modalOpen={!!promptDelete}
                    promptConfirmFn={async () => {
                        await deleteReport({
                            clientId: row.original.platformTenantId,
                            deleteFn: deleteScheduleReport,
                            id: row.original.id,
                            refetch,
                            t
                        });

                        setPromptDelete(false);
                    }}
                    promptCancelFn={() => {
                        setPromptDelete(false);
                    }}
                    promptTitle={t(
                        'Reports.confirmToDeleteScheduledReport.title'
                    )}
                    promptText={
                        <Typography>
                            {t(
                                'Reports.confirmToDeleteScheduledReport.message',
                                { reportName: row.original.name }
                            )}
                        </Typography>
                    }
                    textConfirmButton={t('Common.yes')}
                />
            )}
        </Flex>
    );
};
